"use client"

import styled from "@emotion/styled"
import { Box, Button, Pagination } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useCallback, useMemo, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  extractProperty,
  FacilityContent,
  getBreadCrumbs,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  PortItem,
} from "utils"
import CustomBreadcrumbs from "../CustomBreadcrumbs"
import { ExclamationIcon, Location3Icon, PriceIcon, UserIcon } from "../icons"
import PortContainer from "../PortContainer"
import {
  EMPTY_FILTERS,
  getCtaLabel,
  isMeetingRoom,
  isOffice,
  ITEMS_PER_PAGE,
  SearchFilters,
} from "../PortFacilitiesInnerPage/constants"
import FacilitiesSearchBar from "../PortFacilitiesInnerPage/FacilitiesSearchBar"
import { StyledResultsCount } from "../PortFacilitiesInnerPage/shared"
import PortTitle from "../PortTitle"
import { buildHostSearchParams, useHostAvailability, useHostSearch } from "./useHostSearch"

function buildFacilityUrl(baseUrl: string, filters: SearchFilters): string {
  const url = getNonUmbracoUrl(baseUrl)
  const params = new URLSearchParams()
  if (filters.date) params.set("date", filters.date)
  if (filters.startTime) params.set("startTime", filters.startTime)
  if (filters.endTime) params.set("endTime", filters.endTime)
  if (filters.numberOfPeople.length > 0) params.set("numberOfPeople", filters.numberOfPeople[0])
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

const StyledSection = styled.section<{ $hasBreadcrumbs?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 40px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.port.defaultTextColor};

  padding-block: ${({ $hasBreadcrumbs }) => ($hasBreadcrumbs ? "0" : "60px")} 60px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-block: ${({ $hasBreadcrumbs }) => ($hasBreadcrumbs ? "0" : "120px")} 120px;
  }
`

const StyledItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  width: 100%;
  grid-template-columns: 100%;
  gap: 16px;
  justify-items: center;

  ${(props) => props.theme.breakpoints.up("sm")} {
    gap: 32px;
    grid-template-columns: repeat(2, 1fr);
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  min-height: max-content;

  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.port.grey};
  background: ${(props) => props.theme.colors.white};
  overflow: hidden;
`

const StyledImageWrapper = styled.div`
  width: 100%;
  height: 259px;
  display: flex;
  flex-shrink: 0;
  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`

const StyledItemContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px 24px;
  width: 100%;
  gap: 16px;
  flex: 1;
`

const StyledItemName = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 24px;
  font-weight: 300;
  line-height: 150%;
  text-transform: capitalize;
`

const StyledDataList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`

const StyledDataItem = styled.li`
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  span,
  address {
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 150%;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`

const StyledCardButton = styled(Button)`
  width: 100%;
  height: 40px;
  border-radius: 45px;
  font-size: 18px;
  font-weight: 700;
  text-transform: none;
  margin-top: auto;
`

const StyledEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
`

const StyledEmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  width: 100%;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
`

const StyledEmptyIcon = styled.div`
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 52px;
    height: 50px;
  }
  svg {
    width: 100%;
    height: 100%;
  }
`

const StyledEmptyTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  text-align: center;

  font-size: 20px;
  font-weight: 300;
  line-height: 150%;
  letter-spacing: 0.6px;
  text-transform: capitalize;
  margin-bottom: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const StyledEmptySubtitle = styled.p`
  margin: 0;
  font-weight: 300;
  color: ${(props) => props.theme.colors.port.portBulletColor};
  text-align: center;

  font-size: 14px;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
  }
`

const StyledSuggestions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 24px;
`

const StyledSuggestionsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-block: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 28px;
  }
`

const StyledSuggestionsTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  font-size: 20px;
  font-weight: 700;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const StyledSuggestionsSubtitle = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  font-size: 16px;

  font-weight: 300;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const StyledSwiperWrapper = styled.div`
  width: 100%;
  .swiper {
    padding: 0 20px;
    overflow: visible;
  }
  .swiper-wrapper {
    transition-timing-function: ease-in-out;
  }
  .swiper-slide {
    height: auto;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledSuggestionsPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledSuggestionBullet = styled.button<{ $isActive: boolean }>`
  width: 17px;
  height: 17px;
  padding: 0;
  background-color: transparent;
  border: ${({ $isActive, theme }) =>
    $isActive ? `0.5px solid ${theme.colors.propBlue}` : "none"};
  border-radius: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background-color: ${({ $isActive, theme }) =>
      $isActive ? theme.colors.propBlue : theme.colors.port.grey};
    transition: background-color 0.3s ease;
  }
`

const StyledDesktopSuggestions = styled.div`
  display: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0 60px;

  font-family: var(--rag) !important;
  .MuiPagination-ul {
    gap: 16px;
  }

  .MuiPaginationItem-page {
    font-family: var(--rag);
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
    border-radius: 50%;
    font-size: 16px;
    font-weight: 400;
    min-width: 24px;
    height: 24px;
    border: 1px solid ${({ theme }) => theme.colors.port.grey};
    margin: 0;
  }

  .MuiPaginationItem-page.Mui-selected {
    background-color: #0000fa;
    color: ${({ theme }) => theme.colors.white};
  }
`

type PortFacilitiesLobbyProps = {
  data: any
  ports?: PortItem[]
  pageData?: any
  locale?: string
}

const PortFacilitiesLobby = ({ data, ports, pageData, locale }: PortFacilitiesLobbyProps) => {
  const t = useTranslations("portFacilitiesInnerPage")
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const subtitle = extractProperty(data, "subtitle")
  const cmsEmptyTitle = extractProperty(data, "emptyStateTitle")
  const cmsEmptySubtitle = extractProperty(data, "emptyStateSubtitle")

  const [activeFilters, setActiveFilters] = useState<SearchFilters>(EMPTY_FILTERS)
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const suggestionSwiperRef = useRef<SwiperType | null>(null)
  const [suggestionActiveIndex, setSuggestionActiveIndex] = useState(0)

  const allFacilities: FacilityContent[] = useMemo(() => {
    return (
      ports
        ?.filter((port) => !port.upperTextOurLocation?.toLowerCase().includes("coming soon"))
        .flatMap((port) => port.portFacilities || []) || []
    )
  }, [ports])

  // Pre-fetch HOST availability for meeting room date/time pickers
  const hasMeetingRooms = useMemo(
    () => allFacilities.some((f) => isMeetingRoom(f.facilityType)),
    [allFacilities],
  )
  const cmsResourceIds = useMemo(
    () => allFacilities
      .filter((f) => isMeetingRoom(f.facilityType) && f.hostResourceId)
      .map((f) => f.hostResourceId!),
    [allFacilities],
  )
  const hostAvailability = useHostAvailability(hasMeetingRooms, cmsResourceIds)

  // HOST search: map portName → hostLocationName for API calls
  const hostLocationMap = useMemo(() => {
    const map: Record<string, string> = {}
    ports?.forEach((p) => {
      if (p.portName && p.hostLocationName) {
        map[p.portName] = p.hostLocationName
      }
    })
    return map
  }, [ports])

  const hostSearchParams = useMemo(
    () => (hasSearched ? buildHostSearchParams(activeFilters, hostLocationMap) : null),
    [activeFilters, hasSearched, hostLocationMap],
  )
  // eslint-disable-next-line no-unused-vars
  const { results: hostResults, loading: hostLoading } = useHostSearch(hostSearchParams)

  // Build a map of hostResourceId → HOST price for overlay
  const hostPriceMap = useMemo(() => {
    const map: Record<number, number> = {}
    hostResults.forEach((r) => {
      map[r.id] = r.priceWithVat
    })
    return map
  }, [hostResults])

  // Set of HOST resource IDs that are available (for filtering when HOST searched)
  const hostAvailableIds = useMemo(() => {
    return new Set(hostResults.map((r) => r.id))
  }, [hostResults])

  const filteredFacilities = useMemo(() => {
    if (!hasSearched) return allFacilities

    // Start with Umbraco client-side filtering
    let filtered = allFacilities.filter((f) => {
      if (activeFilters.location.length > 0) {
        const port = ports?.find((p) => f.url?.includes(p.url) || f.parentPortUrl?.includes(p.url))
        if (
          !port?.portName ||
          !activeFilters.location.some((loc) =>
            port.portName!.toLowerCase().includes(loc.toLowerCase()),
          )
        ) {
          return false
        }
      }
      if (
        activeFilters.facilityType.length > 0 &&
        !activeFilters.facilityType.includes(f.facilityType)
      ) {
        return false
      }
      if (
        activeFilters.numberOfPeople.length > 0 &&
        !activeFilters.numberOfPeople.includes(f.amountOfPeople)
      ) {
        return false
      }
      return true
    })

    // If HOST search was used, further filter to only rooms HOST says are available
    if (hostSearchParams && hostResults.length > 0) {
      filtered = filtered.filter((f) => !f.hostResourceId || hostAvailableIds.has(f.hostResourceId))
    }

    return filtered
  }, [
    allFacilities,
    activeFilters,
    hasSearched,
    ports,
    hostSearchParams,
    hostResults,
    hostAvailableIds,
  ])

  const totalPages = Math.ceil(filteredFacilities.length / ITEMS_PER_PAGE)
  const paginatedFacilities = filteredFacilities.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  )

  const activeTypeName = activeFilters.facilityType.length > 0 ? activeFilters.facilityType[0] : ""

  const emptyStateSuggestions = useMemo(() => {
    if (filteredFacilities.length > 0 || !hasSearched) return []
    if (activeFilters.facilityType.length > 0) {
      const searchedType = activeFilters.facilityType[0].toLowerCase()
      return allFacilities
        .filter((f) => f.facilityType?.toLowerCase().includes(searchedType))
        .slice(0, 3)
    }
    return allFacilities.slice(0, 3)
  }, [filteredFacilities.length, hasSearched, allFacilities, activeFilters.facilityType])

  const handleSearch = useCallback((filters: SearchFilters) => {
    const isEmpty =
      filters.location.length === 0 &&
      filters.facilityType.length === 0 &&
      !filters.date &&
      !filters.startTime &&
      !filters.endTime &&
      filters.numberOfPeople.length === 0
    setActiveFilters(filters)
    setHasSearched(!isEmpty)
    setCurrentPage(1)
  }, [])

  const handlePageChange = useCallback((_: any, page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  const breadcrumbs = pageData && locale ? getBreadCrumbs(pageData, locale) : null

  if (!blackTitle && !blueTitle && allFacilities.length === 0) {
    return null
  }

  return (
    <StyledSection $hasBreadcrumbs={!!breadcrumbs}>
      {breadcrumbs && (
        <PortContainer paddingInline={20} sx={{ paddingTop: { xs: "80px", md: "98px" } }}>
          <CustomBreadcrumbs
            current={breadcrumbs.selfObject}
            parents={breadcrumbs.parents}
            desktopFontSize={16}
            mobileFontSize={14}
            desktopFontWeight="300"
            mobileFontWeight="300"
            noPadding
            sx={{
              height: { xs: "26px", md: "40px" },
              display: "flex",
              alignItems: "center",
            }}
          />
        </PortContainer>
      )}
      <PortContainer paddingInline={20}>
        <PortTitle
          normalText={blackTitle}
          highlightedText={blueTitle}
          centered
          subtitle={subtitle || ""}
          subtitleSx={{ fontWeight: { xs: "300" } }}
        />
      </PortContainer>

      <PortContainer paddingInline={20}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "16px", md: "32px" } }}>
          <FacilitiesSearchBar
            ports={ports || []}
            allFacilities={allFacilities}
            onSearch={handleSearch}
            hasSearched={hasSearched}
            filteredCount={filteredFacilities.length}
            hostAvailability={hostAvailability}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {filteredFacilities.length > 0 && (
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <StyledResultsCount>
                  {t("found")} <span>{filteredFacilities.length}</span>{" "}
                  {activeTypeName || t("searchResults")}
                </StyledResultsCount>
              </Box>
            )}
            {hasSearched && filteredFacilities.length === 0 ? (
              <StyledEmptyState>
                <StyledEmptyBox>
                  <StyledEmptyIcon>
                    <ExclamationIcon />
                  </StyledEmptyIcon>
                  <StyledEmptyTitle>{cmsEmptyTitle || t("emptyTitle")}</StyledEmptyTitle>
                  <StyledEmptySubtitle>{cmsEmptySubtitle || t("emptySubtitle")}</StyledEmptySubtitle>
                </StyledEmptyBox>
                {emptyStateSuggestions.length > 0 && (
                  <StyledSuggestions>
                    <StyledSuggestionsBox>
                      <StyledSuggestionsTitle>{t("suggestionsTitle")}</StyledSuggestionsTitle>
                      <StyledSuggestionsSubtitle>
                        {t("suggestionsSubtitle", {
                          type: activeTypeName || t("defaultFacilityType"),
                        })}
                      </StyledSuggestionsSubtitle>
                    </StyledSuggestionsBox>
                    <StyledSwiperWrapper>
                      <Swiper
                        slidesPerView={1}
                        spaceBetween={16}
                        centeredSlides
                        onSwiper={(swiper) => {
                          suggestionSwiperRef.current = swiper
                        }}
                        onSlideChange={(swiper) => setSuggestionActiveIndex(swiper.activeIndex)}
                      >
                        {emptyStateSuggestions.map(
                          (
                            {
                              facilityName,
                              facilityType,
                              image,
                              address,
                              amountOfPeople,
                              price,
                              button,
                            },
                            index,
                          ) => (
                            <SwiperSlide key={`swiper-${index}`}>
                              <StyledItem>
                                <StyledImageWrapper>
                                  {image && (
                                    <Image
                                      src={getUmbracoMediaUrl(image)}
                                      width={325}
                                      height={259}
                                      alt={facilityName || "Facility Image"}
                                    />
                                  )}
                                </StyledImageWrapper>
                                <StyledItemContent>
                                  {facilityName && <StyledItemName>{facilityName}</StyledItemName>}
                                  <StyledDataList>
                                    {address && (
                                      <StyledDataItem>
                                        <Location3Icon />
                                        <address>{address}</address>
                                      </StyledDataItem>
                                    )}
                                    {amountOfPeople && (
                                      <StyledDataItem>
                                        <UserIcon />
                                        <span>{amountOfPeople}</span>
                                      </StyledDataItem>
                                    )}
                                    {price && !isOffice(facilityType) && (
                                      <StyledDataItem>
                                        <PriceIcon />
                                        <span>
                                          {isMeetingRoom(facilityType)
                                            ? `${t("priceFrom")} ${price}`
                                            : price}
                                        </span>
                                      </StyledDataItem>
                                    )}
                                  </StyledDataList>
                                  {button?.url && (
                                    <StyledCardButton
                                      variant="contained"
                                      aria-label="facility detail button"
                                      // @ts-ignore - MUI Button href typing
                                      href={
                                        isMeetingRoom(facilityType)
                                          ? buildFacilityUrl(button.url, activeFilters)
                                          : getNonUmbracoUrl(button.url)
                                      }
                                      onClick={(e: any) => {
                                        const href = isMeetingRoom(facilityType)
                                          ? buildFacilityUrl(button.url, activeFilters)
                                          : getNonUmbracoUrl(button.url)
                                        if (href.includes("#")) {
                                          e.preventDefault()
                                          const anchor = href.split("#")[1]
                                          const element = document.getElementById(anchor)
                                          if (element)
                                            element.scrollIntoView({ behavior: "smooth" })
                                        }
                                      }}
                                    >
                                      {getCtaLabel(facilityType, button.name)}
                                    </StyledCardButton>
                                  )}
                                </StyledItemContent>
                              </StyledItem>
                            </SwiperSlide>
                          ),
                        )}
                      </Swiper>
                      {emptyStateSuggestions.length > 1 && (
                        <StyledSuggestionsPagination>
                          {emptyStateSuggestions.map((_, index) => (
                            <StyledSuggestionBullet
                              key={`bullet-${index}`}
                              $isActive={index === suggestionActiveIndex}
                              onClick={() => suggestionSwiperRef.current?.slideTo(index)}
                              aria-label={`Go to slide ${index + 1}`}
                            />
                          ))}
                        </StyledSuggestionsPagination>
                      )}
                    </StyledSwiperWrapper>
                    <StyledDesktopSuggestions>
                      <StyledItemList>
                        {emptyStateSuggestions.map(
                          (
                            {
                              facilityName,
                              facilityType,
                              image,
                              address,
                              amountOfPeople,
                              price,
                              button,
                            },
                            index,
                          ) => (
                            <StyledItem key={index}>
                              <StyledImageWrapper>
                                {image && (
                                  <Image
                                    src={getUmbracoMediaUrl(image)}
                                    width={325}
                                    height={259}
                                    alt={facilityName || "Facility Image"}
                                  />
                                )}
                              </StyledImageWrapper>
                              <StyledItemContent>
                                {facilityName && <StyledItemName>{facilityName}</StyledItemName>}
                                <StyledDataList>
                                  {address && (
                                    <StyledDataItem>
                                      <Location3Icon />
                                      <address>{address}</address>
                                    </StyledDataItem>
                                  )}
                                  {amountOfPeople && (
                                    <StyledDataItem>
                                      <UserIcon />
                                      <span>{amountOfPeople}</span>
                                    </StyledDataItem>
                                  )}
                                  {price && !isOffice(facilityType) && (
                                    <StyledDataItem>
                                      <PriceIcon />
                                      <span>
                                        {isMeetingRoom(facilityType)
                                          ? `${t("priceFrom")} ${price}`
                                          : price}
                                      </span>
                                    </StyledDataItem>
                                  )}
                                </StyledDataList>
                                {button?.url && (
                                  <StyledCardButton
                                    variant="contained"
                                    aria-label="facility detail button"
                                    // @ts-ignore - MUI Button href typing
                                    href={
                                      isMeetingRoom(facilityType)
                                        ? buildFacilityUrl(button.url, activeFilters)
                                        : getNonUmbracoUrl(button.url)
                                    }
                                    onClick={(e: any) => {
                                      const href = isMeetingRoom(facilityType)
                                        ? buildFacilityUrl(button.url, activeFilters)
                                        : getNonUmbracoUrl(button.url)
                                      if (href.includes("#")) {
                                        e.preventDefault()
                                        const anchor = href.split("#")[1]
                                        const element = document.getElementById(anchor)
                                        if (element) element.scrollIntoView({ behavior: "smooth" })
                                      }
                                    }}
                                  >
                                    {getCtaLabel(facilityType, button.name)}
                                  </StyledCardButton>
                                )}
                              </StyledItemContent>
                            </StyledItem>
                          ),
                        )}
                      </StyledItemList>
                    </StyledDesktopSuggestions>
                  </StyledSuggestions>
                )}
              </StyledEmptyState>
            ) : (
              <StyledItemList>
                {paginatedFacilities.map(
                  (
                    {
                      facilityName,
                      facilityType,
                      image,
                      address,
                      amountOfPeople,
                      price,
                      url: faciUrl,
                      hostResourceId,
                    },
                    index,
                  ) => (
                    <StyledItem key={index}>
                      <StyledImageWrapper>
                        {image && (
                          <Image
                            src={getUmbracoMediaUrl(image)}
                            width={325}
                            height={259}
                            alt={facilityName || "Facility Image"}
                          />
                        )}
                      </StyledImageWrapper>
                      <StyledItemContent>
                        {facilityName && <StyledItemName>{facilityName}</StyledItemName>}
                        <StyledDataList>
                          {address && (
                            <StyledDataItem>
                              <Location3Icon />
                              <address>{address}</address>
                            </StyledDataItem>
                          )}
                          {amountOfPeople && (
                            <StyledDataItem>
                              <UserIcon />
                              <span>{amountOfPeople}</span>
                            </StyledDataItem>
                          )}
                          {!isOffice(facilityType) &&
                            (price || (hostResourceId && hostPriceMap[hostResourceId])) && (
                              <StyledDataItem>
                                <PriceIcon />
                                <span>
                                  {isMeetingRoom(facilityType)
                                    ? `${t("priceFrom")} ${
                                        hostResourceId && hostPriceMap[hostResourceId]
                                          ? `₪${hostPriceMap[hostResourceId].toFixed(0)}`
                                          : price
                                      }`
                                    : price}
                                </span>
                              </StyledDataItem>
                            )}
                        </StyledDataList>
                        {faciUrl && (
                          <StyledCardButton
                            variant="contained"
                            aria-label="facility detail button"
                            // @ts-ignore - MUI Button href typing
                            href={
                              isMeetingRoom(facilityType)
                                ? buildFacilityUrl(faciUrl, activeFilters)
                                : getNonUmbracoUrl(faciUrl)
                            }
                            onClick={(e: any) => {
                              const href = isMeetingRoom(facilityType)
                                ? buildFacilityUrl(faciUrl, activeFilters)
                                : getNonUmbracoUrl(faciUrl)
                              if (href.includes("#")) {
                                e.preventDefault()
                                const anchor = href.split("#")[1]
                                const element = document.getElementById(anchor)
                                if (element) {
                                  element.scrollIntoView({ behavior: "smooth" })
                                }
                              }
                            }}
                          >
                            {getCtaLabel(facilityType, t("learnMore"))}
                          </StyledCardButton>
                        )}
                      </StyledItemContent>
                    </StyledItem>
                  ),
                )}
              </StyledItemList>
            )}
          </Box>
        </Box>
      </PortContainer>

      {totalPages > 1 && !(hasSearched && filteredFacilities.length === 0) && (
        <StyledPaginationContainer>
          <Pagination
            count={totalPages}
            page={currentPage}
            color="primary"
            shape="rounded"
            size="small"
            onChange={handlePageChange}
            siblingCount={1}
            boundaryCount={1}
          />
        </StyledPaginationContainer>
      )}
    </StyledSection>
  )
}

export default PortFacilitiesLobby
